import KoobAuthCard from './KoobAuthCard';
import KoobButton from './KoobButton';
import KoobNavbarAvatar from './KoobNavbarAvatar';
import KoobSpinner from './KoobSpinner';

interface McpConsentOrganization {
  organizationId: string | number;
  displayName: string;
  isGroup?: boolean;
}

interface KoobMcpConsentCardProps {
  logo: string;
  backgroundImage?: string;
  /** The MCP client asking for access; null while the request is decoded. */
  client: { clientName: string; redirectHost: string } | null;
  /** The signed-in user (GET /api/v1/user). */
  user?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    organizations?: McpConsentOrganization[];
  } | null;
  /** `key` is an `mcp:` translation key; `detail` the API's own message. */
  error?: { key: string; detail?: string } | null;
  deciding?: boolean;
  onApprove: () => void;
  onDeny: () => void;
  onSwitchAccount: () => void;
  onSignIn: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}

const PERMISSIONS = [
  'permissionCatalog',
  'permissionBookings',
  'permissionRoles',
];

/**
 * OAuth consent screen for MCP clients (KD-3735): who is signed in, what the
 * client may do and for which organizations, then Deny / Authorize.
 */
export default function KoobMcpConsentCard({
  logo,
  backgroundImage,
  client,
  user,
  error,
  deciding = false,
  onApprove,
  onDeny,
  onSwitchAccount,
  onSignIn,
  t,
}: KoobMcpConsentCardProps) {
  if (error) {
    return (
      <KoobAuthCard logo={logo} backgroundImage={backgroundImage}>
        <div className="flex flex-col items-center text-center">
          <i className="fa-light fa-circle-exclamation text-3xl text-red-500" />
          <p className="mt-4 text-sm text-k-h">{t(`mcp:${error.key}`)}</p>
          {error.detail ? (
            <p className="mt-2 text-xs text-k-p">{error.detail}</p>
          ) : null}
        </div>
        {error.key === 'sessionExpired' ? (
          <div className="mt-8">
            <KoobButton variant="primary" size="lg" block onClick={onSignIn}>
              {t('mcp:signIn')}
            </KoobButton>
          </div>
        ) : null}
      </KoobAuthCard>
    );
  }

  if (!client || !user) {
    return (
      <KoobAuthCard logo={logo} backgroundImage={backgroundImage}>
        <div className="flex justify-center py-10">
          <KoobSpinner />
        </div>
      </KoobAuthCard>
    );
  }

  const { clientName, redirectHost } = client;
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
  const organizations = user.organizations ?? [];

  return (
    <KoobAuthCard
      logo={logo}
      backgroundImage={backgroundImage}
      title={t('mcp:title', { clientName })}
    >
      <div className="mt-6 flex items-center gap-3 rounded-xl border border-gray-200 p-3">
        <KoobNavbarAvatar name={name || user.email} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-k-h">{name}</p>
          <p className="truncate text-xs text-k-p">{user.email}</p>
        </div>
        <button
          type="button"
          className="shrink-0 cursor-pointer border-none bg-transparent p-0 text-xs font-medium text-k-orange hover:underline"
          onClick={onSwitchAccount}
        >
          {t('mcp:notYou')}
        </button>
      </div>

      <h2 className="mt-6 text-sm font-semibold text-k-h">
        {t('mcp:permissionsTitle', { clientName })}
      </h2>
      <ul className="mt-2 space-y-2">
        {PERMISSIONS.map((key) => (
          <li key={key} className="flex items-start gap-2 text-sm text-k-p">
            <i className="fa-light fa-check mt-0.5 w-4 text-green-600" />
            {t(`mcp:${key}`)}
          </li>
        ))}
      </ul>

      <h2 className="mt-6 text-sm font-semibold text-k-h">
        {t('mcp:organizationsTitle', { count: organizations.length })}
      </h2>
      <ul className="mt-2 max-h-40 space-y-2 overflow-y-auto">
        {organizations.map((organization) => (
          <li
            key={organization.organizationId}
            className="flex items-start gap-2 text-sm text-k-p"
          >
            <i
              className={`fa-light ${
                organization.isGroup ? 'fa-buildings' : 'fa-building'
              } mt-0.5 w-4 text-k-p`}
            />
            {organization.isGroup
              ? t('mcp:allOrganizationsOf', { name: organization.displayName })
              : organization.displayName}
          </li>
        ))}
      </ul>

      <div className="mt-8 grid grid-cols-2 gap-3">
        <KoobButton
          variant="secondary"
          size="lg"
          block
          disabled={deciding}
          onClick={onDeny}
        >
          {t('mcp:deny')}
        </KoobButton>
        <KoobButton
          variant="primary"
          size="lg"
          block
          loading={deciding}
          onClick={onApprove}
        >
          {t('mcp:approve')}
        </KoobButton>
      </div>

      <p className="mt-6 text-center text-xs text-k-p">
        {t('mcp:trustNotice', { clientName, host: redirectHost })}
      </p>
    </KoobAuthCard>
  );
}
